import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import HeadTitleKeyword from "@/components/common/HeadTitleKeyword";

import React from "react";

import AccountVerificationBanner, {
  useShouldShowAccountVerificationBanner,
} from "@/components/banners/AccountVerificationBanner";
import DashboardArticles from "@/ui/DashboardArticles";
import DashboardAiAgentBanner from "@/ui/aiAgentBanner/DashboardAiAgentBanner";
import QuickStartOnboarding from "@/components/onboarding/QuickStartOnboarding";
import DashboardProductNavigation from "./dashboard/productNavigation/DashboardProductNavigation";
import useDashboardProductSelection from "./dashboard/productNavigation/useDashboardProductSelection";
import { DashboardProduct } from "./dashboard/productNavigation/useDashboardProducts";

/**
 * The shared Setup Guides / AI agent banner only accompany an active
 * product; canceled and inactive tabs end at their Popular Plans. An unknown
 * selection (plans still loading) keeps them.
 */
function isNonActiveProduct(product: DashboardProduct | undefined) {
  return product !== undefined && product.status !== "active";
}

/**
 * The Setup Guides + AI agent banner below the product tabs. Hidden rather
 * than unmounted while a canceled or inactive tab is shown: unmounted, their
 * icons were fetched and decoded again each time the reader came back to an
 * active tab, popping in one by one (R-2260).
 */
function SharedDashboardSections({ hidden }: { hidden: boolean }) {
  return (
    <Box hidden={hidden}>
      <DashboardArticles />
      <DashboardAiAgentBanner />
    </Box>
  );
}

export default function Dashboard() {
  const theme = useTheme();

  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));

  const shouldShowAccountVerificationBanner =
    useShouldShowAccountVerificationBanner();

  // The dashboard is split by product tabs (r-1776); each product's bandwidth
  // alert sits inside its own tab, below its usage overview.
  const productSelection = useDashboardProductSelection();
  const hidesSharedSections = isNonActiveProduct(
    productSelection.selectedProduct
  );

  return (
    <>
      <HeadTitleKeyword title="Dashboard" />

      <Grid
        container
        direction="row"
        spacing={3}
        sx={{ p: 3, pt: isMobileView ? 3 : 0 }}
      >
        <Grid size={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" color="text.primary">
              Dashboard
            </Typography>
          </Box>
        </Grid>

        <QuickStartOnboarding />

        {shouldShowAccountVerificationBanner && (
          <Box sx={{ width: "100%" }}>
            <AccountVerificationBanner />
          </Box>
        )}

        <DashboardProductNavigation selection={productSelection} />
      </Grid>
      <SharedDashboardSections hidden={hidesSharedSections} />
    </>
  );
}
